import React from 'react';
import { useRouter } from 'next/navigation';
import ActivityIcon from '@/common/components/ActivityIcon';
import { DeleteItemIcon, EditSquareIcon } from '@/assets/icons/button';
import { DraggableDotsVerticalIcon } from '@/assets/ui/dots';
import { CompletionBadge } from '@/common/components/course';
import ProgressUtilityService from '@/services/course/progress-utility-service';
import UserProgressStorageService from '@/services/storage/user-progress-storage-service';
import QuizFormattingService from '@/services/course/quiz-formatting-service';
import { useAppDispatch } from '@/lib/hooks';
import { setUnitId } from '@/lib/slices/unit-slice';
import { setSubunitId } from '@/lib/slices/subunit-slice';
import { setActivityId } from '@/lib/slices/activity-slice';
import { useSetActivity } from '@/common/hooks/useSetActivity';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import {
  ActivityData,
  ActivityProgressCompletionData,
  SubunitData,
  SubunitId,
  UnitId,
} from 'course-types';

const DEFAULT_PROGRESS: ActivityProgressCompletionData = {
  video: {},
  document: {},
  quiz: {},
  assignment: {},
};

interface ActivityRowProps {
  activity: ActivityData;
  unitId: string;
  unitName: string;
  courseName: string;
  courseId: string;
  subunit?: SubunitData & { id: string };
  isAdminView?: boolean;
  isEditable?: boolean;
  dragAttributes?: any;
  dragListeners?: any;
  onClick?: (
    activity: ActivityData,
    subunit?: SubunitData & { id: string },
    unitId?: string,
  ) => void;
  onEdit?: (activity: ActivityData, subunitId?: string) => void;
  disableDelete?: boolean;
}

/**
 * ActivityRow component displays a single activity in a course unit or subunit.
 * Handles both admin and student views with appropriate interactions.
 *
 */
export default function ActivityRow({
  activity,
  unitId,
  unitName,
  courseName,
  courseId,
  subunit,
  isAdminView,
  isEditable = true,
  dragAttributes,
  dragListeners,
  onClick,
  onEdit,
  disableDelete,
}: ActivityRowProps) {
  const router = useRouter();
  const dispatch = useAppDispatch();

  const setActivity = useSetActivity({
    subunit,
    unitName,
    unitId,
    courseName,
    courseId,
  });

  const handleUnlockClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isAdminView) return;

    if (activity.blockingContent) {
      setActivity({
        id: activity.blockingContent.id,
        name: activity.blockingContent.name,
        type: activity.blockingContent.type as
          | 'document'
          | 'video'
          | 'quiz'
          | 'resource'
          | 'assignment',
        description: activity.blockingContent.description,
        subunit_id: activity.blockingContent.subunit_id,
        unit_id: activity.blockingContent.unit_id,
      });
    }
  };

  // Get progress data
  const progressCookie = ProgressUtilityService.validateCourseDataExists(
    UserProgressStorageService.getUserCourseProgressCookie() ?? {},
    courseId as any,
    subunit ? undefined : (unitId as UnitId),
    subunit?.id as SubunitId,
  );

  const progressData = subunit
    ? ((progressCookie as any)[courseId]?.completion_data?.subunits?.[subunit.id as SubunitId] ??
      DEFAULT_PROGRESS)
    : ((progressCookie as any)[courseId]?.completion_data?.units?.[unitId as UnitId] ??
      DEFAULT_PROGRESS);

  const completedText = QuizFormattingService.getCompletedText(
    activity.type,
    progressData,
    activity.id,
  );
  const onClickDisabled = QuizFormattingService.isQuizDisabled(activity, progressData);
  const isCompleted = completedText !== '';

  const selectActivity = async () => {
    setActivity({
      ...activity,
      description: activity.description,
      url: activity.url,
    });
  };

  const handleClick = () => {
    // Don't navigate when in admin view
    if (isAdminView) return;

    if (activity.is_locked) return;

    if (activity.type == 'quiz' && onClickDisabled) return;

    if (onClick) {
      onClick(activity, subunit, unitId);
    } else {
      selectActivity();
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disableDelete) return;
    dispatch(setActivityId(activity.id));
    dispatch(setUnitId(unitId));
    dispatch(setSubunitId(subunit?.id ?? undefined));
    dispatch(setModalVisible(true));
    dispatch(setModalName('DeleteContent'));
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onEdit) {
      onEdit(activity, subunit?.id);
    }
  };

  return (
    <div
      key={activity.id}
      // MOBILE FIX: Reduced gap from gap-4 to gap-2 on mobile, gap-4 on tablet+ (sm:gap-4)
      className={`flex flex-row w-full items-center gap-2 sm:gap-4 justify-between p-2 rounded-lg ${
        isAdminView
          ? 'border border-border_gray_200 cursor-default hover:bg-surface_gray_50'
          : onClickDisabled || activity.is_locked
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer hover:bg-surface_gray_50'
      }`}
      onClick={handleClick}
    >
      {/* MOBILE FIX: Added flex-1 min-w-0 to allow text truncation and responsive behavior */}
      <div className='flex flex-row items-center gap-2 sm:gap-4 flex-1 min-w-0'>
        {isAdminView && isEditable && (
          // MOBILE FIX: Added flex-shrink-0 to prevent drag handle from being squeezed on mobile
          <div {...dragAttributes} {...dragListeners} className='cursor-move flex-shrink-0'>
            <DraggableDotsVerticalIcon className='w-4 h-4 text-text_gray_900' />
          </div>
        )}
        {/* MOBILE FIX: Added flex-shrink-0 to keep activity icon at full size on mobile */}
        <ActivityIcon
          className='aspect-square flex-shrink-0'
          type={activity.type}
          border
          size='small'
        />
        {/* MOBILE FIX: Added flex-1 min-w-0 to allow proper text wrapping and truncation */}
        <div className='flex flex-col gap-1 flex-1 min-w-0'>
          <div className='flex flex-row gap-2 items-start'>
            {/* MOBILE FIX: Removed truncation to show full title on mobile*/}
            <h3 className='text-text_gray_900 text-sm not-italic font-semibold leading-5 flex-1'>
              {activity.name}
            </h3>
            {activity.is_locked && (
              <div
                className='flex flex-row items-center gap-2 px-2 py-0.5 rounded-2xl flex-shrink-0 cursor-pointer'
                onClick={handleUnlockClick}
              >
                <span className='text-text_gray_600 text-xs not-italic font-medium leading-[18px] hover:underline'>
                  Complete {activity.blockingContent?.name}{' '}
                  {activity.blockingContent?.minimum_grade_threshold
                    ? 'with a grade of ' +
                      activity.blockingContent?.minimum_grade_threshold +
                      ' or higher'
                    : ''}{' '}
                  to unlock
                </span>
              </div>
            )}
            <div className='hidden sm:flex justify-center items-center px-2 py-0.5 rounded-2xl bg-surface_gray_100 dark:border dark:border-border_gray_200 flex-shrink-0'>
              <span className='capitalize text-text_gray_700 text-center text-xs not-italic font-medium leading-[18px] whitespace-nowrap'>
                {activity.type}
              </span>
            </div>
          </div>
          {(activity.type == 'quiz' || activity.type == 'assignment') && !isAdminView && (
            <span className='text-text_gray_600 text-start text-xs not-italic font-medium leading-[18px]'>
              {`${activity.due_date ? `${QuizFormattingService.formatQuizDueDate(activity.due_date as Date, (activity.lock_quiz || activity.lock_after_due_date) as boolean)}` : ''}${activity.type == 'quiz' && activity.due_date ? ' • ' : ''} ${QuizFormattingService.formatRemainingAttempts(activity, progressData)}`}
            </span>
          )}
        </div>
      </div>
      {isAdminView && isEditable && (
        // MOBILE FIX: Changed to flex-col on mobile (sm:flex-row on tablet+) to stack buttons vertically
        // This prevents the buttons from overlapping with multi-line activity titles on small screens
        <div className='flex flex-col sm:flex-row items-center gap-1 flex-shrink-0'>
          <button
            onClick={handleEditClick}
            className='flex items-center justify-center p-2 rounded-[8px] w-fit h-fit hover:bg-surface_gray_100 transition-colors'
          >
            <EditSquareIcon className='h-5 w-5 text-[#A4A7AE]' />
          </button>
          <button
            onClick={handleDelete}
            disabled={!!disableDelete}
            className={`flex items-center justify-center p-2 rounded-[8px] w-fit h-fit group transition-colors ${
              disableDelete ? 'opacity-50 cursor-not-allowed' : 'hover:bg-surface_red_100'
            }`}
          >
            <DeleteItemIcon className='h-5 w-5 text-[#A4A7AE] group-hover:text-red-600' />
          </button>
        </div>
      )}
      {!isAdminView && isCompleted && <CompletionBadge text='' />}
    </div>
  );
}
